import { createRef } from "react";
import { Button, CssBaseline, StyledEngineProvider } from "@mui/material";
import { PaletteMode, ThemeProvider, createTheme } from "@mui/material/styles";
import { SnackbarKey, SnackbarProvider } from "notistack";
import { useTranslation } from "react-i18next";
import { QueryClient, QueryClientProvider } from "react-query";
import { Provider } from "react-redux";
import store from "./app/store";
import SnackbarRunner from "./app/snackbar-runner.component";
import PublicVisitorConsentRouter from "./components/Routing/public-visitor-consent-router.component";
import { toValidMuiColor } from "./utils/theme-color.utils";
import { makeSeeditThemeOptions } from "./themes/make-seedit-theme";
import { DEFAULT_BRAND_COLOR } from "./themes/seedit-theme.tokens";
import { configureMobiscrollTheme } from "./themes/sync-mobiscroll-theme";

const queryClient = new QueryClient();

const PUBLIC_CONSENT_BRAND_COLOR = DEFAULT_BRAND_COLOR;
const PUBLIC_CONSENT_COLOR_MODE: PaletteMode = "light";

const publicConsentThemeOptions = makeSeeditThemeOptions(
  PUBLIC_CONSENT_COLOR_MODE,
  toValidMuiColor(PUBLIC_CONSENT_BRAND_COLOR)
);

configureMobiscrollTheme(PUBLIC_CONSENT_COLOR_MODE, PUBLIC_CONSENT_BRAND_COLOR);

const SnackbarAction = (
  key: SnackbarKey,
  onClickDismiss: (key: SnackbarKey) => () => void,
  t: (key: string) => string
) => (
  <Button
    size="small"
    style={{ color: "white", pointerEvents: "auto" }}
    variant={"text"}
    onClick={onClickDismiss(key)}
  >
    {t("Got it")}
  </Button>
);

/** Anonymous visitor consent entry — no Keycloak session or authenticated API bootstrap. */
export const PublicVisitorConsentBootstrap: React.FC = () => {
  const { t } = useTranslation();
  const notistackRef = createRef<SnackbarProvider>();
  const onClickDismiss = (key: SnackbarKey) => () => {
    notistackRef.current?.closeSnackbar(key);
  };

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={createTheme(publicConsentThemeOptions)}>
            <SnackbarProvider
              ref={notistackRef}
              action={key => SnackbarAction(key, onClickDismiss, t)}
              maxSnack={3}
            >
              <SnackbarRunner />
              <CssBaseline />
              <PublicVisitorConsentRouter />
            </SnackbarProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      </QueryClientProvider>
    </Provider>
  );
};
